/* eslint-disable no-unused-vars */
class SearchDropdown {
  constructor() {
    this.dropdown = document.getElementById('search-dropdown');
    if (!this.dropdown) return;

    this.input = document.getElementById('search-input');
    this.overlay = document.getElementById('search-overlay');
    this.loading = document.getElementById('search-loading');
    this.clearBtn = document.getElementById('search-clear');
    this.closeBtn = document.getElementById('search-close');
    this.results = document.getElementById('search-results');
    this.noResults = document.getElementById('search-no-results');
    this.initialState = document.getElementById('search-initial');
    this.categoriesList = document.getElementById('categories-list');
    this.productsList = document.getElementById('products-list');
    this.recipeBlogsList = document.getElementById('recipe-blogs-list');
    this.translations = {
      errorMessage: this.dropdown.dataset.errorMessage || 'Error',
      noResults: this.dropdown.dataset.noResults || 'Keine Ergebnisse',
      searchOpened: this.dropdown.dataset.searchOpened || 'Search opened',
      searchClosed: this.dropdown.dataset.searchClosed || 'Search closed',
    };
    this.currency = this.dropdown.dataset.currency || 'EUR';
    // Use Shopify's global routes or fall back to data attribute
    let routesRoot =
      window.Shopify?.routes?.root || this.dropdown.dataset.routesRoot || '/';
    routesRoot = routesRoot.trim();
    if (!routesRoot.endsWith('/')) {
      routesRoot += '/';
    }
    this.routesRoot = routesRoot;

    // Store original no results HTML
    this.originalNoResultsHtml = this.noResults ? this.noResults.innerHTML : '';

    this.isOpen = false;
    this.searchTimeout = null;
    this.currentQuery = '';

    this.init();
  }

  init() {
    if (!this.dropdown) return;
    this.bindEvents();
    this.setupKeyboardNavigation();
  }

  bindEvents() {
    // Toggle dropdown
    document.querySelectorAll('[data-search-toggle]').forEach(button => {
      button.addEventListener('click', e => {
        e.preventDefault();
        this.toggle();
      });
    });

    // Input events
    this.input.addEventListener('input', e => {
      this.handleInput(e.target.value);
    });

    // Clear button
    this.clearBtn.addEventListener('click', () => {
      this.clearSearch();
    });

    // Close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => {
        this.close();
      });
    }

    // Overlay click (mobile)
    this.overlay.addEventListener('click', () => {
      this.close();
    });

    // Click outside to close
    document.addEventListener('click', e => {
      if (
        this.isOpen &&
        !this.dropdown.contains(e.target) &&
        !e.target.closest('[data-search-toggle]')
      ) {
        this.close();
      }
    });

    // Escape key to close
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
  }

  setupKeyboardNavigation() {
    this.dropdown.addEventListener('keydown', e => {
      if (!['ArrowDown', 'ArrowUp'].includes(e.key)) return;

      // Get focusable elements only from the currently visible tab
      const activeTabPanel = this.dropdown.querySelector(
        '.search-tab-panels > div:not(.hidden)'
      );
      if (!activeTabPanel) return;

      const focusableElements = activeTabPanel.querySelectorAll(
        'a[href], button:not([disabled])'
      );

      if (focusableElements.length === 0) return;

      const currentIndex = Array.from(focusableElements).indexOf(
        document.activeElement
      );

      e.preventDefault();
      if (e.key === 'ArrowDown') {
        const nextIndex =
          currentIndex < focusableElements.length - 1 ? currentIndex + 1 : 0;
        focusableElements[nextIndex]?.focus();
      } else {
        const prevIndex =
          currentIndex > 0 ? currentIndex - 1 : focusableElements.length - 1;
        focusableElements[prevIndex]?.focus();
      }
    });
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.isOpen = true;
    this.dropdown.classList.remove('hidden');
    this.overlay.classList.remove('hidden');

    // Ensure clean state when opening
    this.hideAllSections();
    // Reset no results to original HTML
    if (this.noResults && this.originalNoResultsHtml) {
      this.noResults.innerHTML = this.originalNoResultsHtml;
    }
    if (this.initialState) {
      this.initialState.classList.remove('hidden');
    }

    this.input.focus();
    document.body.classList.add('overflow-hidden');

    // Prevent sticky header from moving when dropdown is open
    const header = document.querySelector('.js-sticky-header');
    if (header) {
      header.classList.remove('-translate-y-full');
    }

    // Announce to screen readers
    SearchDropdown.announceToScreenReader(this.translations.searchOpened);
  }

  close() {
    this.isOpen = false;
    this.dropdown.classList.add('hidden');
    this.overlay.classList.add('hidden');
    this.input.blur();
    this.clearSearch();
    document.body.classList.remove('overflow-hidden');

    // Restore sticky header behavior when dropdown is closed
    const header = document.querySelector('.js-sticky-header');
    if (header) {
      // Let the sticky header script handle its own behavior
      // We just ensure it's not locked in the visible state
    }

    // Announce to screen readers
    SearchDropdown.announceToScreenReader(this.translations.searchClosed);
  }

  handleInput(value) {
    this.currentQuery = value.trim();

    // Show/hide clear button
    if (this.currentQuery) {
      this.clearBtn.classList.remove('hidden');
    } else {
      this.clearBtn.classList.add('hidden');
      return;
    }

    // Debounce search
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.performSearch();
    }, 300);
  }

  async performSearch() {
    if (!this.currentQuery) return;

    this.showLoading();

    try {
      // Use the correct Shopify predictive search API endpoint with locale-aware path
      const searchUrl = `${this.routesRoot}search/suggest.json?q=${encodeURIComponent(this.currentQuery)}&resources[type]=product,collection,article&resources[limit]=10&resources[options][unavailable_products]=last`;
      // Debug: log the search URL
      // eslint-disable-next-line no-console
      console.log('Search URL:', searchUrl, 'routesRoot:', this.routesRoot);
      const response = await fetch(searchUrl);

      if (!response.ok) {
        // eslint-disable-next-line no-console
        console.error(
          'Search API error:',
          response.status,
          response.statusText
        );
        throw new Error(`Search request failed: ${response.status}`);
      }

      const data = await response.json();
      this.displayResults(data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Search error:', error);
      this.showError();
    } finally {
      this.hideLoading();
    }
  }

  displayResults(data) {
    this.hideAllSections();

    // Hide initial state when showing results
    if (this.initialState) {
      this.initialState.classList.add('hidden');
    }

    // Handle the correct Shopify API response format
    const products = data.resources?.results?.products || [];
    const collections = data.resources?.results?.collections || [];
    const recipeBlogs = data.resources?.results?.articles || [];

    const hasResults =
      products.length > 0 || collections.length > 0 || recipeBlogs.length > 0;

    if (!hasResults) {
      this.showNoResults();
      return;
    }

    // Initialize tab group
    const tabGroup = this.dropdown.querySelector('.search-tab-group');
    if (tabGroup) {
      // Show the tab group
      tabGroup.classList.remove('hidden');

      // Update tab headings with result counts
      this.updateTabCounts(
        products.length,
        collections.length,
        recipeBlogs.length
      );

      // Set up tab functionality
      this.setupTabs();

      // Always clear all lists first
      this.clearAllResultLists();

      // Determine which tab to show based on results
      const hasProducts = products.length > 0;
      const hasCollections = collections.length > 0;
      const hasRecipes = recipeBlogs.length > 0;

      // Display products or empty state
      if (hasProducts) {
        this.displayProducts(products);
        SearchDropdown.hideEmptyState('products');
      } else {
        SearchDropdown.showEmptyState('products');
      }

      // Display categories or empty state
      if (hasCollections) {
        this.displayCategories(collections);
        SearchDropdown.hideEmptyState('categories');
      } else {
        SearchDropdown.showEmptyState('categories');
      }

      // Display recipe blogs or empty state
      if (hasRecipes) {
        this.displayRecipeBlogs(recipeBlogs);
        SearchDropdown.hideEmptyState('recipe-blogs');
      } else {
        SearchDropdown.showEmptyState('recipe-blogs');
      }

      // Tab selection logic:
      // If no products AND no categories found, automatically select recipe-blogs tab
      // Otherwise, show products tab if available, then categories, then recipe-blogs
      if (!hasProducts && !hasCollections && hasRecipes) {
        this.showTab('recipe-blogs');
      } else if (hasProducts) {
        this.showTab('products');
      } else if (hasCollections) {
        this.showTab('categories');
      } else if (hasRecipes) {
        this.showTab('recipe-blogs');
      }
    }
  }

  clearAllResultLists() {
    this.productsList.innerHTML = '';
    this.categoriesList.innerHTML = '';
    this.recipeBlogsList.innerHTML = '';
    // Hide all empty states
    SearchDropdown.hideEmptyState('products');
    SearchDropdown.hideEmptyState('categories');
    SearchDropdown.hideEmptyState('recipe-blogs');
  }

  static showEmptyState(tabName) {
    const emptyStateId =
      tabName === 'recipe-blogs' ? 'recipe-blogs-empty' : `${tabName}-empty`;
    const emptyState = document.getElementById(emptyStateId);
    if (emptyState) {
      emptyState.classList.remove('hidden');
    }
  }

  static hideEmptyState(tabName) {
    const emptyStateId =
      tabName === 'recipe-blogs' ? 'recipe-blogs-empty' : `${tabName}-empty`;
    const emptyState = document.getElementById(emptyStateId);
    if (emptyState) {
      emptyState.classList.add('hidden');
    }
  }

  displayCategories(categories) {
    this.categoriesList.innerHTML = '';

    categories.forEach(category => {
      const categoryElement = document.createElement('a');
      categoryElement.href = category.url;
      categoryElement.className =
        'search-category-card flex items-center p-3 group';
      const imageUrl = category.featured_image?.url || '';

      categoryElement.innerHTML = `
        <div class="w-16 h-16 mr-4 flex items-center justify-center overflow-hidden">
          ${
            imageUrl
              ? `<img src="${imageUrl}" alt="${SearchDropdown.escapeHtml(category.featured_image?.alt || category.title)}" class="w-full h-full object-cover">`
              : `<svg class="w-8 h-8 text-aubergine/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
            </svg>`
          }
        </div>
        <div>
          <div class="link-medium text-aubergine link-group-hover-bold">${SearchDropdown.escapeHtml(category.title)}</div>
        </div>
      `;
      this.categoriesList.appendChild(categoryElement);
    });
  }

  displayProducts(products) {
    this.productsList.innerHTML = '';

    products.forEach(product => {
      const productElement = document.createElement('a');
      productElement.href = product.url;
      productElement.className =
        'search-product-card flex items-center p-3 group';

      const imageUrl = product.featured_image?.url || product.image || '';
      // Shopify predictive search API returns price in cents
      // Try price_min first (for products with variations), then price, then price_max
      const rawPrice = product.price_min || product.price || product.price_max;
      const rawCompareAtPrice =
        product.compare_at_price_min || product.compare_at_price;

      const price = rawPrice ? this.formatPrice(rawPrice) : '';
      const compareAtPrice = rawCompareAtPrice
        ? this.formatPrice(rawCompareAtPrice)
        : '';

      // Check if compare_at_price exists and is greater than regular price
      const hasComparePrice =
        rawCompareAtPrice && rawPrice && rawCompareAtPrice > rawPrice;

      // Build price HTML similar to PDP (but keep original size p-medium)
      let priceHtml = '';
      if (price) {
        if (hasComparePrice && compareAtPrice) {
          priceHtml = `
            <div class="flex items-center gap-2">
              <span class="p-medium text-aubergine line-through">${compareAtPrice}</span>
              <span class="p-medium text-pflaume">${price}</span>
            </div>
          `;
        } else {
          priceHtml = `<div class="p-medium text-pflaume">${price}</div>`;
        }
      }

      productElement.innerHTML = `
        <div class="w-16 h-16 mr-4 flex items-center justify-center overflow-hidden">
          ${
            imageUrl
              ? `<img src="${imageUrl}" alt="${SearchDropdown.escapeHtml(product.title)}" class="w-full h-full object-cover">`
              : `<svg class="w-8 h-8 text-aubergine/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
            </svg>`
          }
        </div>
        <div class="flex flex-col">
          <div class="link-medium text-aubergine mb-1 link-group-hover-bold">${SearchDropdown.escapeHtml(product.title)}</div>
          <div class="p-small text-aubergine/70 mb-1">${SearchDropdown.escapeHtml(product.vendor || '')}</div>
          ${priceHtml}
        </div>
      `;
      this.productsList.appendChild(productElement);
    });
  }

  displayRecipeBlogs(recipeBlogs) {
    this.recipeBlogsList.innerHTML = '';

    recipeBlogs.forEach(article => {
      const articleElement = document.createElement('a');
      articleElement.href = article.url;
      articleElement.className =
        'search-recipe-blog-card flex items-center p-3 group';

      const imageUrl = article.featured_image?.url || article.image || '';

      articleElement.innerHTML = `
        <div class="w-16 h-16 mr-4 flex items-center justify-center overflow-hidden">
          ${
            imageUrl
              ? `<img src="${imageUrl}" alt="${SearchDropdown.escapeHtml(article.title)}" class="w-full h-full object-cover">`
              : `<svg class="w-8 h-8 text-aubergine/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
            </svg>`
          }
        </div>
        <div>
          <div class="link-medium text-aubergine link-group-hover-bold">${SearchDropdown.escapeHtml(article.title)}</div>
        </div>
      `;
      this.recipeBlogsList.appendChild(articleElement);
    });
  }

  updateTabCounts(productsCount, categoriesCount, recipeBlogsCount) {
    // Store counts for later use
    this.tabCounts = {
      products: productsCount,
      categories: categoriesCount,
      recipeBlogs: recipeBlogsCount,
    };

    // Update products tab
    const productsTab = this.dropdown.querySelector('[data-tab="products"]');
    if (productsTab) {
      const originalText = productsTab.getAttribute('data-original-text');
      productsTab.innerHTML = `${originalText} (${productsCount})`;
    }

    // Update categories tab
    const categoriesTab = this.dropdown.querySelector(
      '[data-tab="categories"]'
    );
    if (categoriesTab) {
      const originalText = categoriesTab.getAttribute('data-original-text');
      categoriesTab.innerHTML = `${originalText} (${categoriesCount})`;
    }

    // Update recipe blogs tab
    const recipeBlogsTab = this.dropdown.querySelector(
      '[data-tab="recipe-blogs"]'
    );
    if (recipeBlogsTab) {
      const originalText = recipeBlogsTab.getAttribute('data-original-text');
      recipeBlogsTab.innerHTML = `${originalText} (${recipeBlogsCount})`;
    }
  }

  showLoading() {
    this.loading.classList.remove('hidden');
  }

  hideLoading() {
    this.loading.classList.add('hidden');
  }

  showNoResults() {
    this.hideAllSections();
    if (this.initialState) {
      this.initialState.classList.add('hidden');
    }
    this.noResults.classList.remove('hidden');
  }

  showError() {
    this.hideAllSections();
    if (this.initialState) {
      this.initialState.classList.add('hidden');
    }
    // Use a separate error element or temporarily show error in noResults
    this.noResults.innerHTML = `<p class="text-red-500 p-big">${this.translations.errorMessage}</p>`;
    this.noResults.classList.remove('hidden');
  }

  hideAllSections() {
    this.noResults.classList.add('hidden');

    // Hide tab group
    const tabGroup = this.dropdown.querySelector('.search-tab-group');
    if (tabGroup) {
      tabGroup.classList.add('hidden');
    }
  }

  setupTabs() {
    const tabButtons = this.dropdown.querySelectorAll('[data-tab]');
    const tabPanels = this.dropdown.querySelectorAll(
      '.search-tab-panels > div'
    );

    tabButtons.forEach(button => {
      button.addEventListener('click', () => {
        const tabName = button.getAttribute('data-tab');
        this.showTab(tabName);
      });
    });
  }

  showTab(tabName) {
    // Ensure tab counts are preserved
    this.refreshTabCounts();

    // Hide all tab panels
    const tabPanels = this.dropdown.querySelectorAll(
      '.search-tab-panels > div'
    );
    tabPanels.forEach(panel => {
      panel.classList.add('hidden');
    });

    // Remove active state from all tab buttons
    const tabButtons = this.dropdown.querySelectorAll('[data-tab]');
    tabButtons.forEach(button => {
      button.classList.remove('text-aubergine');
      button.classList.add('text-aubergine/70');
    });

    // Show the selected tab panel
    const targetPanel = this.dropdown.querySelector(`#search-${tabName}`);
    if (targetPanel) {
      targetPanel.classList.remove('hidden');
    }

    // Activate the selected tab button
    const targetButton = this.dropdown.querySelector(`[data-tab="${tabName}"]`);
    if (targetButton) {
      targetButton.classList.remove('text-aubergine/70');
      targetButton.classList.add('text-aubergine');
    }
  }

  refreshTabCounts() {
    if (this.tabCounts) {
      this.updateTabCounts(
        this.tabCounts.products,
        this.tabCounts.categories,
        this.tabCounts.recipeBlogs
      );
    }
  }

  clearSearch() {
    this.input.value = '';
    this.currentQuery = '';
    this.clearBtn.classList.add('hidden');
    // Hide search results and show initial state
    this.hideAllSections();
    // Reset no results to original HTML (in case error modified it)
    if (this.noResults && this.originalNoResultsHtml) {
      this.noResults.innerHTML = this.originalNoResultsHtml;
    }
    if (this.initialState) {
      this.initialState.classList.remove('hidden');
    }
  }

  formatPrice(price) {
    // Shopify predictive search API typically returns prices in cents
    // However, the API might return prices in different formats
    let priceValue;

    if (typeof price === 'string') {
      // Remove any formatting characters and parse
      const cleaned = price.replace(/[^\d.,-]/g, '').replace(',', '.');
      priceValue = parseFloat(cleaned);
    } else {
      priceValue = price;
    }

    // If price is less than 1000, it's likely already in decimal format
    // (most products cost at least 10 EUR = 1000 cents)
    // Otherwise, treat it as cents and divide by 100
    const priceInDecimal = priceValue < 1000 ? priceValue : priceValue / 100;

    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: this.currency,
    }).format(priceInDecimal);
  }

  static escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  static announceToScreenReader(message) {
    // Create announcement element for screen readers
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    document.body.appendChild(announcement);

    // Remove announcement after screen reader has time to read it
    setTimeout(() => {
      if (document.body.contains(announcement)) {
        document.body.removeChild(announcement);
      }
    }, 1000);
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const searchDropdown = new SearchDropdown();
  // Store reference to prevent garbage collection
  window.searchDropdown = searchDropdown;
});
